import React from 'react';
import MessageLabel from '../CommonComponents/MessageLabel';
import MessageSubHeading from '../CommonComponents/MessageSubHeading';
import MessageHeading from '../CommonComponents/MessageHeading';

const DeviceSelector = ({ 
  selectedDevices = [], 
  onDeviceChange, 
  gatewayName = '', 
  availableDevices = ['web', 'Android', 'iOS', 'TV'],
  showDescriptions = true,
  className = '',
  label = 'Supported Devices',
  showSelectAll = true,
  showPaymentKeyDetails = false,
  paymentMode = 'TEST',
  keyDetailsComponent = null,
  showDeviceStats = true,
  cardStyle = 'default' // 'default', 'bordered', 'minimal'
}) => {
  
  // Debug log to check props
  console.log('DeviceSelector props:', { selectedDevices, availableDevices, gatewayName });

  // Device configuration with enhanced icons and descriptions
  const deviceConfig = {
    web: { 
      name: 'Web', 
      icon: '🌐', 
      description: 'Web browsers',
      color: '#007bff'
    },
    Android: { 
      name: 'Android', 
      icon: '📱', 
      description: 'Android devices',
      color: '#28a745'
    },
    iOS: { 
      name: 'iOS', 
      icon: '📱', 
      description: 'iOS devices',
      color: '#6f42c1'
    },
    TV: { 
      name: 'TV', 
      icon: '📺', 
      description: 'Smart TV apps',
      color: '#fd7e14'
    }
  };

  const handleDeviceToggle = (deviceId, checked) => {
    let updatedDevices;
    
    if (checked) {
      // Add device if not already present
      updatedDevices = selectedDevices.includes(deviceId) 
        ? selectedDevices 
        : [...selectedDevices, deviceId];
    } else {
      // Remove device
      updatedDevices = selectedDevices.filter(device => device !== deviceId);
    }
    
    console.log('DeviceSelector - Toggle:', { deviceId, checked, selectedDevices, updatedDevices });
    if (onDeviceChange) {
      onDeviceChange(updatedDevices);
    }
  };

  const handleSelectAll = () => {
    const newSelection = selectedDevices.length === availableDevices.length 
      ? [] 
      : [...availableDevices];
      
    console.log('DeviceSelector - Select All:', { selectedDevices, availableDevices, newSelection });
    if (onDeviceChange) {
      onDeviceChange(newSelection);
    }
  };

  const allSelected = selectedDevices.length === availableDevices.length;
  const noneSelected = selectedDevices.length === 0;

  // Function to render key details section if enabled
  const renderKeyDetailsSection = () => {
    if (!showPaymentKeyDetails) return null;
    
    const capitalizedGatewayName = gatewayName ? 
      gatewayName.charAt(0).toUpperCase() + gatewayName.slice(1) : 'Payment Gateway';
    
    return (
      <div className="payment-key-details mt-4 mb-3">
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <MessageHeading text={`${capitalizedGatewayName} Key Details (${paymentMode})`} />
            <small className="text-muted d-block">
              {paymentMode === 'TEST' ? 
                'Use these keys for testing and development' : 
                'Live keys for production environment'}
            </small>
          </div>
          <div className="card-body">
            {keyDetailsComponent}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className={`device-selector ${className}`}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <MessageLabel text={label} className="" />
        {showSelectAll && (
          <button 
            type="button"
            className={`btn btn-sm ${allSelected ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>
      
      {!noneSelected && showDeviceStats && (
        <MessageSubHeading 
          text={`${selectedDevices.length} of ${availableDevices.length} devices selected`} 
          className="mb-2 text-muted small" 
          noMargin 
        />
      )}
      
      <div className="row mb-3">
        {availableDevices.map((device) => {
          const isChecked = selectedDevices.includes(device);
          const deviceId = gatewayName ? `${gatewayName}_${device}` : `device_${device}`;
          const config = deviceConfig[device] || { 
            name: device, 
            icon: '📱', 
            description: device,
            color: '#6c757d'
          };
          
          return (
            <div key={device} className="col-6 col-md-3 mb-3">
              <div className={`card h-100 ${
                cardStyle === 'bordered' ? 'border-2' :
                cardStyle === 'minimal' ? 'border-0 bg-light' :
                'shadow-sm'
              } ${isChecked ? 'border-primary' : ''}`}>
                <div className="card-body p-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id={deviceId}
                      className="form-check-input"
                      checked={isChecked}
                      onChange={(e) => handleDeviceToggle(device, e.target.checked)}
                    />
                    <label htmlFor={deviceId} className="form-check-label w-100">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span style={{ fontSize: '1.2em', color: config.color || '#6c757d' }}>{config.icon}</span>
                        <strong style={{ color: isChecked ? config.color : '#495057' }}>{config.name}</strong>
                      </div>
                      {showDescriptions && (
                        <small className="text-muted d-block">
                          {config.description}
                        </small>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {noneSelected && (
        <div className="alert alert-warning" role="alert">
          <small>
            ⚠️ No devices selected. This payment gateway will be disabled for all platforms.
          </small>
        </div>
      )}
      
      {/* Render payment key details section if enabled */}
      {renderKeyDetailsSection()}
    </div>
  );
};

export default DeviceSelector;